import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Navbar from "../navbar/Navbar";
import "./Machinerecords.css";
import "../form/ReactSelect.css";

import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { appendAlert } from "../../redux/alertSlice";

import { PlantCodeSelect } from "../ui/PlantCodeSelect";
import { ProcCodeSelect } from "../ui/ProcCodeSelect";
import { MachCodeSelect } from "../ui/MachCodeSelect";
import RippleButton from "../ui/rippleButton";
import Footer from "../footer/Footer";
//
import apiAxios from "../../utils/apiAxios";
//
import Grid from "../grid/MachineRecordsGrid";
import Spinner from "../ui/Spinner";
import { GridApi } from "ag-grid-enterprise";
import { forEach } from "lodash";
import { GetMachRecords } from "../../models/GetMachRecords";
import { isAuthorized } from "../../utils/permissionUtils";
import { useNavigate } from "react-router-dom";

interface PostMachrecord {
  plant_code: string;
  id: number;
  proc_code: string;
  mach_code: string;
  start_time: string;
  end_time: string;
  duration: number;
  update_by: string;
}

const Machinerecords: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();

  // ログインユーザーID
  const userId = useSelector(
    (state: RootState) => state.user.userInfo?.user_id ?? ""
  );
  // 権限
  const permissions = useSelector(
    (state: RootState) => state.user.userInfo?.permissions ?? []
  );
  // 機械打刻修正権限が存在する場合のみ表示可能
  useEffect(() => {
    const authorized = isAuthorized("1", permissions);
    if (!authorized && userId !== "") {
      dispatch(
        appendAlert({
          severity: "error",
          message: "機械打刻を修正する権限はありません。",
          displayTime: 5000,
        })
      );
      navigate("/");
    }
  }, [userId]);

  const plantCode = useSelector(
    (state: RootState) => state.queryParams.selectedPlantCode
  ); // 製造工場コード
  const procCode = useSelector(
    (state: RootState) => state.queryParams.selectedProcCode
  ); // 加工コード
  const machCode = useSelector(
    (state: RootState) => state.queryParams.machCode
  ); // 機械作業コード

  // 初期値を設定
  const [selectPlantCode, setSelectPlantCode] = useState(plantCode); // 工場コード
  const [selectedProcCode, setSelectedProcCode] = useState(procCode); // 加工コード
  const [selectedMachCode, setSelectedMachCode] = useState(
    machCode && machCode.length === 1 ? machCode[0] : ""
  ); // 機械作業コード

  // セレクトボックスの変更を検知しstateの状態を更新
  // 工場コードの変更を検知
  const handlePlantCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (hasEditData()) {
      if (
        !window.confirm(
          "変更されたデータがあります。破棄してもよろしいですか？"
        )
      ) {
        return false;
      }
    }
    setSelectPlantCode(e.target.value);
    setSelectedProcCode("");
    setSelectedMachCode("");
    fetchRowData(gridApi!, e.target.value, null, null);
  };
  // 加工コードの変更を検知
  const handleProcCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (hasEditData()) {
      if (
        !window.confirm(
          "変更されたデータがあります。破棄してもよろしいですか？"
        )
      ) {
        return false;
      }
    }
    setSelectedProcCode(e.target.value);
    setSelectedMachCode("");
    fetchRowData(gridApi!, selectPlantCode, e.target.value, null);
  };
  // 機械作業コードの変更を検知
  const handleMachCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (hasEditData()) {
      if (
        !window.confirm(
          "変更されたデータがあります。破棄してもよろしいですか？"
        )
      ) {
        return false;
      }
    }
    setSelectedMachCode(e.target.value);
    fetchRowData(gridApi!, selectPlantCode, selectedProcCode, e.target.value);
  };

  // GridのAPI
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const fetchRowData = async (
    api: GridApi,
    plant_code: string | null,
    proc_code: string | null,
    mach_code: string | null
  ) => {
    api.setRowData([]);
    if (!plant_code) {
      return;
    }
    let data = await getMachRecords(plant_code, proc_code, mach_code);
    if (data) {
      data = data.map((elem: any) => {
        return {
          ...elem,
          is_add: false,
          is_edit_start_time: false,
          is_edit_end_time: false,
          is_edit_duration: false,
          is_error_start_time: false,
          is_error_end_time: false,
          is_error_duration: false,
          error_message: "",
        };
      });
      api.applyTransaction({ add: data });
    }
  };

  // Gridの初期化時のコールバック
  const onGridReady = (params: { api: GridApi }) => {
    setGridApi(params.api);
    (async () => {
      await fetchRowData(
        params.api,
        selectPlantCode,
        selectedProcCode,
        selectedMachCode
      );
      params.api.sizeColumnsToFit();
    })();
  };

  const [isLoading, setIsLoading] = useState(false);

  // 機械実績の取得
  const getMachRecords = async (
    plant_code: string | null,
    proc_code: string | null,
    mach_code: string | null
  ) => {
    try {
      setIsLoading(true);
      const response = await apiAxios.get<GetMachRecords[]>(`/mach_records`, {
        params: {
          plant_code: plant_code,
          proc_code: proc_code,
          mach_code: mach_code,
        },
      });
      // 終了時刻が入っていないものは修正対象外として除く
      return response.data.filter((d) => d.end_time !== "");
    } catch (error: any) {
      console.error(error);
      dispatch(
        appendAlert({
          severity: "error",
          message: "データの取得に失敗しました。" + error.message,
          displayTime: 5000,
        })
      );
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // 編集行があるかチェックする
  const hasEditData = () => {
    let hasEdit = false;
    gridApi!.forEachNode((node: any) => {
      let data = node.data;
      let isEdit = data.is_add;
      forEach(data, (value, key) => {
        if (key.startsWith("is_edit_")) {
          isEdit = isEdit || value;
        }
      });
      hasEdit = hasEdit || isEdit;
    });
    return hasEdit;
  };

  // 機械実績の登録
  const registMachRecords = async () => {
    let rowData: any[] = [];
    gridApi!.forEachNode((node: any) => {
      // 変更・追加された行のみを取得
      let data = node.data;

      let isEdit = data.is_add;
      forEach(data, (value, key) => {
        if (key.startsWith("is_edit_")) {
          isEdit = isEdit || value;
        }
      });
      if (isEdit) {
        rowData.push(data);
      }
    });

    if (rowData.length === 0) {
      dispatch(
        appendAlert({
          severity: "error",
          message: "変更されたデータがありません。",
          displayTime: 5000,
        })
      );
      return;
    }

    // エラーがあるかチェックする
    let isError = false;
    rowData.forEach((row: any) => {
      forEach(row, (value, key) => {
        if (key.startsWith("is_error_") && value) {
          isError = true;
        }
      });
    });

    if (isError) {
      dispatch(
        appendAlert({
          severity: "error",
          message:
            "入力エラーがあります。★が付いている行のデータを確認してください。",
          displayTime: 5000,
        })
      );
      return;
    }

    let postData: PostMachrecord[] = [];
    rowData.forEach((elem: any) => {
      if (elem.id < 0) {
        // 新規追加行
        postData.push({
          plant_code: elem.plant_code,
          id: 0,
          proc_code: elem.proc_code,
          mach_code: elem.mach_code,
          start_time: elem.start_time,
          end_time: elem.end_time,
          duration: elem.duration,
          update_by: userId,
        });
      } else {
        // 変更行
        postData.push({
          plant_code: elem.plant_code,
          id: elem.id,
          proc_code: elem.proc_code,
          mach_code: elem.mach_code,
          start_time: elem.start_time,
          end_time: elem.end_time,
          duration: elem.duration,
          update_by: userId,
        });
      }
    });

    // 機械実績の登録
    try {
      await apiAxios.post(`/mach_records`, postData);
    } catch (error: any) {
      console.error(error);
      dispatch(
        appendAlert({
          severity: "error",
          message: "データの登録に失敗しました。。" + error.message,
          displayTime: 5000,
        })
      );
      return;
    }

    // データを再取得する
    await fetchRowData(
      gridApi!,
      selectPlantCode,
      selectedProcCode,
      selectedMachCode
    );

    dispatch(
      appendAlert({
        severity: "success",
        message: "データを登録しました。",
        displayTime: 5000,
      })
    );
  };

  return (
    <div>
      <Helmet>
        <title>Process Flow Manager</title>
      </Helmet>
      <Navbar />
      <div className="machrecords-container">
        <div className="machrecords-container-title">
          <img src="images/clock.svg" alt="" />
          <img src="images/machine.svg" alt="" />
          <h1>機械打刻修正</h1>
        </div>

        <div className="machrecords-container-navi">
          <div className="machrecords-container-navi-item">
            <PlantCodeSelect
              label="工場"
              selectPlantCode={selectPlantCode}
              handlePlantCodeChange={handlePlantCodeChange}
            />
          </div>

          <div className="machrecords-container-navi-item">
            <ProcCodeSelect
              label="加工"
              plantCode={selectPlantCode}
              selectProcCode={selectedProcCode}
              handleProcCodeChange={handleProcCodeChange}
            />
          </div>
          <div className="machrecords-container-navi-item">
            <MachCodeSelect
              label="機械"
              plantCode={selectPlantCode}
              procCode={selectedProcCode}
              selectMachCode={selectedMachCode}
              handleMachCodeChange={handleMachCodeChange}
            />
          </div>
        </div>
        <div className="machrecords-container-grid">
          {isLoading && <Spinner />}
          <Grid onGridReadyParent={onGridReady} />
        </div>
        <div className="machrecords-reg-btn">
          <RippleButton
            width={"300px"}
            heihgt={"45px"}
            borderRadius={"8px"}
            onClick={registMachRecords}
            effectSize={160}
            className="bk-secondary"
          >
            <span className="children">登録</span>
          </RippleButton>
        </div>
      </div>
      {/* フッター */}
      <Footer />
    </div>
  );
};

export default Machinerecords;
